import React, { useState, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Gamepad2, Play, Search, Clock, Zap, Users, Star, Mic, Heart, Music, Timer } from "lucide-react"; // Added Timer icon

// --- Import Images ---
import starzoo from "../Photos/starzoo.png"
import whisper from "../Photos/whistper.png"
import echo from "../Photos/echoquest.png"
import emoji from "../Photos/Emojimatch.png"
import vibe from "../Photos/viberacer.png"
import twister from "../Photos/twistertalk.png"
import counting from "../Photos/couningcastle.png"
import mood from "../Photos/moodmixer.png"
import storyp from "../Photos/story.png"
import timeclock from "../Photos/clock.png"
import secret from "../Photos/secretshield.png"


// =====================================================
// 🎯 DATA - ENHANCED with STATUS
// =====================================================
const GAME_PATH_MAP = {
    "Whisper Woods 🌳": "/playgames/whisperwoods",
    "Echo Quest 🎵": "/playgames/echoquest",
    "Emoji Match Mania 🧩": "/playgames/emojimatch",
    "Vibe Racer 🚀": "/playgames/viberacer",
    "Twister Talk 🗣️": "/playgames/twistertalk",
    "Counting Castle 🔢": "/playgames/countingcastle",
    // Coming Soon games should not have a valid path for navigation
};

const ALL_GAMES = [
    { id: 1, title: "Whisper Woods 🌳", desc: "Wake forest friends using soft sounds! Focuses on low volume and gentle phonetics.", btn: "Start Quietly", category: "Phonics & Speech", image: whisper, color: "from-green-300 to-teal-400", difficulty: "Easy", playTime: "5 min", usersPlayed: 1245, status: "live" },
    { id: 2, title: "Echo Quest 🎵", desc: "Repeat rhythms to solve puzzles! Great for timing and pitch recognition.", btn: "Begin Echo Quest", category: "Rhythm & Music", image: echo, color: "from-purple-300 to-indigo-400", difficulty: "Medium", playTime: "7 min", usersPlayed: 980, status: "live" },
    { id: 3, title: "Emoji Match Mania 🧩", desc: "Match pairs of emojis! Great for visual memory and focus.", btn: "Start Matching", category: "Early Math & Logic", image: emoji, color: "from-pink-300 to-red-300", difficulty: "Easy", playTime: "4 min", usersPlayed: 2100, status: "live" },
    { id: 4, title: "Vibe Racer 🚀", desc: "Use loud words to speed up! A dynamic game for loud, clear articulation.", btn: "Start Racing", category: "High Energy", image: vibe, color: "from-red-300 to-orange-400", difficulty: "Medium", playTime: "6 min", usersPlayed: 750, status: "coming_soon" },
    { id: 5, title: "Twister Talk 🗣️", desc: "Say tricky tongue twisters! Improves articulation and fluency.", btn: "Try Twister", category: "Phonics & Speech", image: twister, color: "from-green-300 to-teal-300", difficulty: "Hard", playTime: "8 min", usersPlayed: 450, status: "coming_soon" },
    
    // Games 6 to 11 are COMING SOON
    { id: 6, title: "Counting Castle 🔢", desc: "Count aloud to build towers! Reinforces early math and quantity concepts.", btn: "Start Counting", category: "Early Math & Logic", image: counting, color: "from-yellow-300 to-orange-300", difficulty: "Easy", playTime: "5 min", usersPlayed: 1800, status: "coming_soon" },
    { id: 7, title: "Mood Melody Mixer 😊", desc: "Change scenes with emotions! Builds emotional vocabulary and tone recognition.", btn: "Mix Mood", category: "Creativity & Emotion", image: mood, color: "from-red-300 to-pink-300", difficulty: "Medium", playTime: "7 min", usersPlayed: 620, status: "coming_soon" },
    { id: 8, title: "Star Zoo Sort 🔬", desc: "Describe funny creatures! Focuses on descriptive language and classification.", btn: "Sort the Zoo", category: "Science & Classification", image: starzoo, color: "from-blue-300 to-cyan-300", difficulty: "Medium", playTime: "10 min", usersPlayed: 580, status: "coming_soon" },
    { id: 9, title: "Story Palette 🎨", desc: "Create magical stories! Encourages imaginative speech and narrative skills.", btn: "Open Sketchpad", category: "Creativity & Emotion", image: storyp, color: "from-pink-300 to-fuchsia-300", difficulty: "Hard", playTime: "12 min", usersPlayed: 310, status: "coming_soon" },
    { id: 10, title: "Time Traveler Clock ⏰", desc: "Answer time questions! Teaches clock reading and sequencing.", btn: "Travel Time", category: "Early Math & Logic", image: timeclock, color: "from-yellow-300 to-amber-300", difficulty: "Hard", playTime: "9 min", usersPlayed: 490, status: "coming_soon" },
    { id: 11, title: "Secret Spy Shield 🛡️", desc: "Whisper secret passwords! Encourages careful, clear speech.", btn: "Activate Shield", category: "Phonics & Speech", image: secret, color: "from-green-300 to-blue-300", difficulty: "Medium", playTime: "6 min", usersPlayed: 850, status: "coming_soon" },
];

const CATEGORIES = [
    { name: "All Games", icon: Gamepad2 },
    { name: "Phonics & Speech", icon: Mic },
    { name: "Early Math & Logic", icon: Star },
    { name: "Creativity & Emotion", icon: Heart },
    { name: "Rhythm & Music", icon: Music },
    { name: "High Energy", icon: Zap },
    { name: "Science & Classification", icon: Users },
];

// --- FRAMER MOTION (Unchanged) ---
const cardItemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100, damping: 12 } },
};

// =====================================================
// 💳 GAME CARD - ENHANCED LAYOUT
// =====================================================
const GameCard = ({ game, handleNavigate }) => {
    
    const isComingSoon = game.status === "coming_soon";

    const getDifficultyColor = (difficulty) => {
        switch (difficulty) {
            case 'Easy': return 'bg-green-500';
            case 'Medium': return 'bg-yellow-500';
            case 'Hard': return 'bg-red-500';
            default: return 'bg-gray-500';
        }
    };
    
    const formatCount = (count) => {
        return count > 999 ? (count / 1000).toFixed(1) + 'k' : count;
    };

    const handleCardClick = (e) => {
        if (isComingSoon) {
            e.stopPropagation(); // Prevent card click
            console.log(`${game.title} is coming soon!`);
        } else {
            handleNavigate(game.title);
        }
    };

    return (
        <motion.div
            variants={cardItemVariants}
            className={`flex flex-col bg-white rounded-3xl shadow-xl overflow-hidden transition-all duration-300 
                ${isComingSoon 
                    ? "opacity-60 grayscale cursor-not-allowed" 
                    : "hover:shadow-2xl transform hover:scale-[1.02] cursor-pointer"
                }`}
            onClick={handleCardClick}
        >
            {/* 1. Thumbnail Image */}
            <div className="relative h-72 bg-white rounded-t-3xl overflow-hidden flex items-center justify-center">
                {game.image ? (
                    <>
                        <img
                            src={game.image}
                            alt={game.title}
                            className="w-full h-full object-contain p-2"
                        />

                        {/* Featured Stripe */}
                        <div
                            className={`absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-r ${game.color} opacity-80`}
                            aria-hidden="true"
                        />

                        {/* COMING SOON BANNER */}
                        {isComingSoon && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/30 backdrop-blur-sm">
                                <motion.div
                                    initial={{ scale: 0.5, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{ type: "spring", stiffness: 300, damping: 15 }}
                                    className="bg-yellow-500 text-black py-2 px-6 rounded-full font-extrabold text-xl shadow-xl flex items-center gap-2 transform rotate-1"
                                >
                                    <Timer className="w-5 h-5" />
                                    Coming Soon
                                </motion.div>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="h-full flex items-center justify-center bg-gray-200 text-gray-500 text-lg font-bold">
                        No Image
                    </div>
                )}
            </div>


            <div className="p-6 flex flex-col flex-grow">
                {/* Title & Category */}
                <h2 className="text-2xl font-extrabold text-gray-900 mb-2 leading-tight">{game.title}</h2>
                <span className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-semibold w-fit mb-4 uppercase tracking-wider">
                    {game.category}
                </span>

                {/* Description */}
                <p className="text-gray-600 text-sm flex-grow mb-4 line-clamp-3">
                    {game.desc}
                </p>

                {/* 2. Stats Section */}
                <div className="flex justify-between items-center text-sm text-gray-500 border-t pt-4 mt-auto">
                    {/* Difficulty */}
                    <div className="flex items-center gap-1">
                        <Star className={`w-4 h-4 ${getDifficultyColor(game.difficulty)} text-white rounded-full p-0.5`} />
                        <span className="font-semibold">{game.difficulty}</span>
                    </div>

                    {/* Play Time */}
                    <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span>{game.playTime}</span>
                    </div>

                    {/* Users Played */}
                    <div className="flex items-center gap-1">
                        <Users className="w-4 h-4 text-gray-400" />
                        <span>{formatCount(game.usersPlayed)} Plays</span>
                    </div>
                </div>

                {/* 3. Call to Action Button */}
                <motion.button
                    className={`w-full text-white py-3 rounded-2xl font-bold transition mt-6 text-lg shadow-lg
                        ${isComingSoon 
                            ? "bg-gray-400 cursor-not-allowed shadow-gray-200" 
                            : "bg-blue-600 hover:bg-blue-700 shadow-blue-300"
                        }`}
                    onClick={(e) => { e.stopPropagation(); handleCardClick(e); }} // Use handleCardClick for consistent logic
                    whileHover={isComingSoon ? {} : { scale: 1.01 }}
                    whileTap={isComingSoon ? {} : { scale: 0.99 }}
                    disabled={isComingSoon}
                >
                    {isComingSoon ? (
                        <>
                            <Timer className="inline w-5 h-5 mr-2" />
                            Notify Me!
                        </>
                    ) : (
                        <>
                            <Play className="inline w-5 h-5 mr-2" />
                            {game.btn}
                        </>
                    )}
                </motion.button>
            </div>
        </motion.div>
    );
};

// =====================================================
// 🖥️ MAIN PAGE - FULL SCREEN LAYOUT (Unchanged)
// =====================================================
export default function TalkyToonPlayPage() {
    const [searchTerm, setSearchTerm] = useState("");
    const [activeCategory, setActiveCategory] = useState("All Games");
    const navigate = useNavigate();

    const handleNavigate = useCallback((title) => {
        if (GAME_PATH_MAP[title]) navigate(GAME_PATH_MAP[title]);
        else console.log(`Navigation path not defined for: ${title}`);
    }, [navigate]);

    const filteredGames = useMemo(() => {
        return ALL_GAMES.filter((g) =>
            (activeCategory === "All Games" || g.category === activeCategory) &&
            (g.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                g.desc.toLowerCase().includes(searchTerm.toLowerCase()))
        );
    }, [searchTerm, activeCategory]);

    return (
        // The container uses min-h-screen and no max-width to utilize full viewport
        <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">

            {/* Content Wrapper - Using padding for internal spacing but keeping it full width */}
            <div className="py-8 px-4 sm:px-6 lg:px-8">
                
                {/* Header - Centered and full width */}
                <div className="text-center mb-10">
                    <h1 className="text-5xl font-extrabold text-blue-700 tracking-tight mb-2">
                        🌟 Game Adventures
                    </h1>
                    <p className="text-lg text-gray-600">Choose your next interactive voice challenge!</p>
                </div>


                {/* MOBILE CATEGORY SCROLL */}
                {/* Still needs overflow-x-auto, but the padding is now applied by the parent div */}
                <div className="flex overflow-x-auto gap-3 pb-6 border-b mb-6 lg:hidden">
                    {CATEGORIES.map((c) => (
                        <button
                            key={c.name}
                            onClick={() => { setActiveCategory(c.name); setSearchTerm(''); }}
                            className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap font-semibold text-sm shadow transition duration-200 ${activeCategory === c.name
                                ? "bg-blue-600 text-white shadow-md shadow-blue-300"
                                : "bg-white text-gray-700 border hover:bg-blue-50"
                                }`}
                        >
                            <c.icon className="w-4 h-4" />
                            {c.name}
                        </button>
                    ))}
                </div>

                {/* Desktop Category Filter & Search Bar */}
                <div className="flex flex-col lg:flex-row gap-8">
                    
                    {/* Desktop Category Filter */}
                    <div className="hidden lg:block lg:w-64 flex-shrink-0">
                        <h3 className="text-lg font-bold text-gray-700 mb-4 border-b pb-2">Filter by Skill:</h3>
                        <div className="space-y-2 sticky top-4"> {/* Added sticky top-4 for better UX */}
                            {CATEGORIES.map((c) => (
                                <button
                                    key={c.name}
                                    onClick={() => { setActiveCategory(c.name); setSearchTerm(''); }}
                                    className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg font-semibold text-left text-base transition duration-200 ${activeCategory === c.name
                                        ? "bg-blue-100 text-blue-700 ring-2 ring-blue-300 shadow-md"
                                        : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-100"
                                        }`}
                                >
                                    <c.icon className="w-5 h-5" />
                                    {c.name}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Search Bar & Grid (Main Area) */}
                    <div className="flex-1">
                        <div className="relative mb-8 max-w-4xl mx-auto lg:mx-0"> {/* Constrain search bar width */}
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-500 w-6 h-6" />
                            <input
                                type="text"
                                placeholder={`Search games in the ${activeCategory === 'All Games' ? 'entire library...' : activeCategory + ' focus...'}`}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full py-4 pl-14 pr-4 rounded-2xl shadow-lg text-lg border border-blue-200 focus:ring-4 focus:ring-blue-100"
                            />
                        </div>

                        {/* GRID DISPLAY */}
                        <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-2">
                            {activeCategory} ({filteredGames.length})
                        </h2>

                        {/* Grid container uses full available width */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-8">
                            {filteredGames.length > 0 ? (
                                filteredGames.map((game) => (
                                    <GameCard key={game.id} game={game} handleNavigate={handleNavigate} />
                                ))
                            ) : (
                                <div className="col-span-full text-center py-16 bg-gray-100 rounded-xl">
                                    <Zap className="w-10 h-10 text-gray-400 mx-auto mb-4" />
                                    <p className="text-lg text-gray-700">No games found matching your search or filter.</p>
                                </div>
                            )}
                        </div>
                    </div>

                </div>

            </div>

        </div>
    );
}